import React from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  handleClearCacheAndReload = async () => {
    try {
      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const name of cacheNames) {
          await caches.delete(name);
        }
      }
      // Clear localStorage cache entries (keep auth token)
      const token = localStorage.getItem('token');
      localStorage.clear();
      if (token) localStorage.setItem('token', token);
      // Force hard reload
      window.location.reload(true);
    } catch (e) {
      console.error('Cache clear failed:', e);
      window.location.reload(true);
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
            <p className="text-muted-foreground mb-4">
              We encountered an unexpected error. Try clearing cache first, or refresh the page.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={this.handleClearCacheAndReload} variant="destructive" className="w-full">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Cache & Reload
              </Button>
              <Button onClick={this.handleReset} className="w-full bg-forest-900 hover:bg-forest-800">
                <RefreshCw className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </div>
            {this.state.error && (
              <div className="mt-4">
                <button
                  onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
                  className="text-xs text-muted-foreground underline"
                >
                  {this.state.showDetails ? 'Hide' : 'Show'} error details
                </button>
                {this.state.showDetails && (
                  <pre className="mt-2 p-3 bg-muted rounded-lg text-left text-xs overflow-auto max-h-48 break-words whitespace-pre-wrap">
                    {this.state.error.toString()}
                    {this.state.error.stack && ('\n\n' + this.state.error.stack)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
